import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { apiClient } from '../services/apiClient';
import { useAuth } from '../hooks/useAuth';

const Screen = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.background};
  padding: 24px;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: 16px;
  padding: 32px;
  width: 100%;
  max-width: 440px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  text-align: center;

  h1 { font-size: 24px; font-weight: 800; color: ${({ theme }) => theme.colors.text}; margin-bottom: 6px; }
  .price { font-size: 40px; font-weight: 800; color: ${({ theme }) => theme.colors.primary}; margin: 12px 0 2px; }
  .price small { font-size: 15px; color: ${({ theme }) => theme.colors.textMuted}; font-weight: 600; }
  p { color: ${({ theme }) => theme.colors.textSecondary}; font-size: 14px; line-height: 1.55; margin-bottom: 16px; }
  ul { text-align: left; color: ${({ theme }) => theme.colors.text}; font-size: 14px; margin: 0 auto 20px; max-width: 320px; line-height: 1.7; padding-left: 18px; }
`;

const LogoutLink = styled.button`
  background: none; border: none; color: ${({ theme }) => theme.colors.textMuted}; font-size: 13px;
  cursor: pointer; margin-top: 16px; text-decoration: underline;
  &:hover { color: ${({ theme }) => theme.colors.textSecondary}; }
`;

const ErrorMsg = styled.div`
  background: #FEF2F2; border: 1px solid #FECACA; color: #991B1B;
  border-radius: 8px; padding: 10px; font-size: 13px; margin-top: 12px;
`;

interface PaypalConfig { clientId: string | null; planId: string | null; enabled: boolean }

// Pantalla de pago: muestra el botón de suscripción de PayPal. Al aprobar,
// verifica contra el server (activate) y refresca el acceso.
export function Paywall() {
  const { logout, refreshSubscription, user } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const renderedRef = useRef(false);
  const [config, setConfig] = useState<PaypalConfig | null>(null);
  const [error, setError] = useState('');
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    apiClient.get<PaypalConfig>('/api/billing/paypal-config')
      .then(setConfig)
      .catch(() => setError('Could not load payment configuration.'));
  }, []);

  useEffect(() => {
    if (!config?.enabled || !config.clientId || !config.planId) return;

    const SDK_ID = 'paypal-sdk';
    const render = () => {
      const paypal = (window as unknown as { paypal?: any }).paypal;
      if (!paypal || !containerRef.current || renderedRef.current) return;
      renderedRef.current = true;
      try {
        paypal.Buttons({
          style: { shape: 'rect', color: 'gold', layout: 'vertical', label: 'subscribe' },
          createSubscription: (_data: unknown, actions: any) =>
            // custom_id = userId liga la suscripción a esta cuenta (anti-compartir).
            actions.subscription.create({ plan_id: config.planId, custom_id: user?.id }),
          onApprove: async (data: { subscriptionID?: string }) => {
            setActivating(true);
            setError('');
            try {
              await apiClient.post('/api/billing/paypal/activate', { subscriptionID: data.subscriptionID });
              // PayPal puede tardar unos segundos en pasar a ACTIVE: reintentamos.
              let active = await refreshSubscription();
              for (let i = 0; i < 10 && !active; i++) {
                await new Promise((r) => setTimeout(r, 3000));
                active = await refreshSubscription();
              }
              if (!active) setError('Your payment went through but the subscription is not active yet. Wait a moment and reload the page.');
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Could not activate the subscription.');
            } finally {
              setActivating(false);
            }
          },
          onError: () => setError('There was a problem with PayPal. Please try again.'),
        }).render(containerRef.current);
      } catch {
        setError('Could not initialize the PayPal button.');
      }
    };

    const existing = document.getElementById(SDK_ID) as HTMLScriptElement | null;
    if ((window as unknown as { paypal?: unknown }).paypal) {
      render();
    } else if (existing) {
      existing.addEventListener('load', render);
    } else {
      const s = document.createElement('script');
      s.id = SDK_ID;
      s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(config.clientId)}&vault=true&intent=subscription`;
      s.onload = render;
      s.onerror = () => setError('Could not load PayPal.');
      document.body.appendChild(s);
    }
  }, [config, refreshSubscription]);

  return (
    <Screen>
      <Card>
        <h1>Coopen SMM</h1>
        <p>Hi{user?.name ? ` ${user.name}` : ''} 👋 You need an active subscription to use the platform.</p>
        <div className="price">$20 <small>/ month</small></div>
        <ul>
          <li>Unlimited post and ad generation</li>
          <li>AI-styled posters, Image to Image and gallery</li>
          <li>All AIs included (no need to bring your own API key)</li>
          <li>Up to 5 collaborators included (they don't pay extra)</li>
          <li>Cancel anytime</li>
        </ul>

        {config && !config.enabled && (
          <ErrorMsg>Payment is not configured yet. Please contact the admin.</ErrorMsg>
        )}

        <div ref={containerRef} style={{ minHeight: 50, opacity: activating ? 0.5 : 1 }} />
        {activating && <p style={{ marginTop: 8 }}>Verifying your payment with PayPal… this can take ~30s.</p>}
        {error && <ErrorMsg>{error}</ErrorMsg>}

        <p style={{ marginTop: 14, fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
          Secure payment via PayPal (card or balance). Manage or cancel anytime from your
          PayPal account and keep access until the end of the paid period.
        </p>

        <LogoutLink onClick={() => logout()}>Log out</LogoutLink>
      </Card>
    </Screen>
  );
}
